import { useEffect, useRef } from 'react';

import { useStoreContext } from 'features/store';

import { toaster } from 'components/toaster/toaster';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { LoyaltyOffer } from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';
import { logger } from 'utils/logger';

import { useServiceOffers } from '../state/service-offers-context';
import { getAppliedOfferEvictionCandidates } from '../utils/applied-offer-ids';
import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

import { useEnableLoyaltyServiceOffers } from './use-enable-loyalty-service-offers';
import { AppliedOffersBatchData } from './use-loyalty-service-applied-offers';

interface UseLoyaltyServiceAppliedOfferEvictionArgs {
  /**
   * Evicts a single applied loyalty-service offer from every cart
   * representation. Owned by the OrderProvider, which knows whether the minimal
   * cart is active and how each store keys the offer:
   * - `cartId`: legacy cart entry id + Redux applied-offer record key — omitted
   *   for a minimal-cart-only offer (no `appliedOffers` record);
   * - `id`: the `loyaltyEngineId`, used to match the minimal-cart
   *   discount/incentive entry via its `referenceId`.
   *
   * This hook stays representation-agnostic: absence from the settled batch is
   * the sole eviction signal, and the callback is a pure sink.
   */
  evictAppliedOffer: (args: { cartId?: string; id: string }) => void;
}

/**
 * Collect any `errors[].code` on a returned offer that should never appear on
 * one: the loyalty-service backend only returns offers whose errors are fixable
 * cart-requirement codes. A code missing from `FixableEvaluationErrors` (whether
 * a known terminal code or an unknown string) is a contract violation to
 * observe — never to evict on.
 */
function getUnexpectedErrorCodes(offer: LoyaltyOffer | undefined): string[] {
  const errors = offer?.errors as ReadonlyArray<{ code?: string | null }> | undefined;
  if (!errors?.length) {
    return [];
  }
  return errors
    .map(error => error?.code)
    .filter(
      (code): code is string =>
        !!code && !FixableEvaluationErrors.has(code as IncentiveEvaluationErrorCodes)
    );
}

/**
 * Evicts applied loyalty-service offers from the cart when the loyalty service
 * is enabled.
 *
 * The single eviction trigger is **absence from the settled applied-offers batch
 * response**: the backend omits unavailable offers, so a returned (present)
 * offer is always kept and an applied offer missing from the settled response is
 * evicted from the cart.
 *
 * Membership keys on the response-derived id set (`data.presentOfferIds`), never
 * Apollo cache existence — the normalized offer entity persists in the cache
 * after the batch response stops returning it.
 *
 * Guards (never evict on a transient/ambiguous state):
 * - not ready (loading / network or GraphQL error / not yet settled) → `data` is
 *   `null` → no eviction;
 * - settled response keyed on a prior store / service mode → stale, no eviction;
 * - an applied id not in the settled request `ids` (a just-applied offer whose
 *   `network-only` refetch is still loading) → no eviction until its response
 *   lands.
 *
 * Present offers are additionally screened by a **non-evicting telemetry
 * guard**: a present offer carrying a non-fixable/unknown `errors[].code`
 * contradicts the backend contract, so we log it and keep the offer.
 *
 * When at least one offer is evicted for a settled batch, an informative toast
 * (`removeExpiredOfferItem`) is shown once — preserving parity with the legacy
 * front-end recompute path (`useRemoveExpiredOfferFromCart`), which is bypassed
 * while the loyalty service is enabled. It fires at most once per settled batch
 * so removing several offers together never stacks duplicate toasts.
 *
 * Inert unless the loyalty service is enabled; the legacy cart eviction stays in
 * `state/order`.
 */
export function useLoyaltyServiceAppliedOfferEviction({
  evictAppliedOffer,
}: UseLoyaltyServiceAppliedOfferEvictionArgs): void {
  const { enabled } = useEnableLoyaltyServiceOffers();
  const { appliedOffersBatch } = useServiceOffers();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);

  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const liveStoreId = store?.number ?? undefined;
  const liveServiceMode = getLoyaltyServiceServiceMode(serviceMode);

  // Emit the telemetry guard at most once per settled batch response (keyed on
  // the settled `data` object reference, which is replaced on each settle).
  const telemetryEmittedForRef = useRef<AppliedOffersBatchData | null>(null);
  // Show the eviction toast at most once per settled batch (same keying), so a
  // settle that delists several offers never stacks duplicate notifications.
  const toastedForRef = useRef<AppliedOffersBatchData | null>(null);

  const settled = appliedOffersBatch.data;

  useEffect(() => {
    if (!enabled || !settled) {
      return;
    }

    // Stale-variable guard: a response settled against a prior store / service
    // mode must not drive eviction for the live context.
    if (
      settled.settledVariables.storeId !== liveStoreId ||
      settled.settledVariables.serviceMode !== liveServiceMode
    ) {
      return;
    }

    // Non-evicting telemetry guard — observe present offers whose codes violate
    // the backend contract. Runs once per settled response.
    if (telemetryEmittedForRef.current !== settled) {
      telemetryEmittedForRef.current = settled;
      Object.entries(settled.metadataById).forEach(([loyaltyEngineId, offer]) => {
        const unexpectedErrorCodes = getUnexpectedErrorCodes(offer);
        if (unexpectedErrorCodes.length) {
          logger.warn({
            message:
              'Loyalty-service returned a present offer carrying an unexpected (non-fixable/unknown) error code; keeping offer (absence is the sole eviction signal)',
            loyaltyEngineId,
            unexpectedErrorCodes,
          });
        }
      });
    }

    const settledIds = new Set(settled.settledVariables.ids);

    // Scan both cart representations, not just `appliedOffers`: a favorited offer
    // wrapper is added to the minimal cart with no Redux record (reorder/recent
    // decompose offers into their child items, so they never take this path). The
    // batch queries the union of ids from both, so eviction must too — otherwise a
    // minimal-cart-only offer the backend omits is queried but never evicted.
    const candidates = getAppliedOfferEvictionCandidates(appliedOffers, minimalCartEntries);

    let evictedAny = false;
    candidates.forEach(({ id, cartId }) => {
      if (!id) {
        return;
      }
      // Only evaluate offers the settled response was actually asked about. A
      // newly-applied id not yet in `settledVariables.ids` is mid-refetch — keep.
      if (!settledIds.has(id)) {
        return;
      }
      // Present => keep (regardless of `errors[]`). Absent => the backend
      // delisted it => evict from every cart representation. `cartId` is absent
      // for a minimal-cart-only offer; the sink clears its minimal-cart row by `id`.
      if (!settled.presentOfferIds.has(id)) {
        evictAppliedOffer({ id, cartId });
        evictedAny = true;
      }
    });

    // Notify the user once per settled batch that an unavailable offer was
    // removed — parity with the bypassed legacy recompute path. Keyed on the
    // settled reference so removing several offers together shows a single toast
    // and a re-run against the same settled batch never re-notifies.
    if (evictedAny && toastedForRef.current !== settled) {
      toastedForRef.current = settled;
      toaster.informative(t('removeExpiredOfferItem'));
    }
  }, [
    enabled,
    settled,
    appliedOffers,
    minimalCartEntries,
    liveStoreId,
    liveServiceMode,
    evictAppliedOffer,
  ]);
}
